// components/EscalationModals.js
// Shared dialogs for the Escalations & Claims module:
// create/edit case drawer, follow-up modal, resolve modal, reject modal, order history modal.
// Used by both the register (dashboard) and the case detail page.
import React, { useEffect, useState } from "react";
import {
  Alert, Button, DatePicker, Drawer, Form, Input, List, Modal, Select, Space, Tag, Upload, message,
} from "antd";
import {
  CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  ACTION_TYPES, CHANNELS, CARRIERS, OPEN_STATUSES, STATUSES, STATUS_META,
  addFollowUp, createCase, errMsg, fetchOrderHistory, rejectCase, resolveCase,
  updateCase, validateOrder,
} from "../services/escalationService";

const { TextArea } = Input;

export const fmtDate = (v) => (v ? dayjs(v).format("DD MMM YYYY") : "—");
export const fmtDateTime = (v) => (v ? dayjs(v).format("DD MMM YYYY, hh:mm A") : "—");

export const statusTag = (status) => {
  const meta = STATUS_META[status] || STATUS_META["Need to Follow Up"];
  const icon = status === "Resolved" ? <CheckCircleOutlined />
    : status === "Rejected" ? <CloseCircleOutlined />
    : status === "Waiting for Response" ? <ClockCircleOutlined />
    : <ExclamationCircleOutlined />;
  return (
    <Tag
      icon={icon}
      style={{
        color: meta.color, background: meta.bg, border: `1px solid ${meta.border}`,
        borderRadius: 999, fontWeight: 600, fontSize: 12, padding: "2px 10px",
      }}
    >
      {status}
    </Tag>
  );
};

const startOfDayIso = (d) => (d ? d.startOf("day").toISOString() : null);

// ─────────────────────────────────────────────
// Create / Edit case drawer
// ─────────────────────────────────────────────
export const EscalationFormDrawer = ({ open, onClose, record, options, onSaved, isMobile = false }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [orderCheck, setOrderCheck] = useState(null);
  const [files, setFiles] = useState([]);
  const isEdit = Boolean(record && record._id);
  const statusValue = Form.useWatch("status", form);
  const carrierValue = Form.useWatch("carrier", form);

  useEffect(() => {
    if (!open) return;
    setFiles([]);
    setOrderCheck(null);
    if (isEdit) {
      form.setFieldsValue({
        ...record,
        escalationDate: record.escalationDate ? dayjs(record.escalationDate) : dayjs(),
        nextFollowUpDate: record.nextFollowUpDate ? dayjs(record.nextFollowUpDate) : null,
        resolutionDate: record.resolutionDate ? dayjs(record.resolutionDate) : null,
        closedDate: record.closedDate ? dayjs(record.closedDate) : null,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        status: "Need to Follow Up",
        escalationDate: dayjs(),
        actionType: "Escalation",
        escalatedVia: "WhatsApp",
      });
    }
  }, [open, record, isEdit, form]);

  const handleValidateOrder = async () => {
    const orderId = form.getFieldValue("salesOrderId");
    if (!orderId) return;
    try {
      const res = await validateOrder(undefined, { orderId, awb: form.getFieldValue("awbCode") || "" });
      if (res.found) {
        const shipment = (res.order.shipments || []).find((s) => s.awb_code && s.awb_code !== "N/A");
        if (shipment && !form.getFieldValue("awbCode")) form.setFieldsValue({ awbCode: shipment.awb_code });
        setOrderCheck({
          type: "success",
          text: `Order found — customer: ${res.order.customer?.name || "—"}${shipment ? ` · carrier: ${shipment.courier_name}` : ""}`,
        });
      } else {
        setOrderCheck({ type: "warning", text: res.message });
      }
    } catch (err) {
      setOrderCheck({ type: "error", text: errMsg(err, "Order validation failed") });
    }
  };

  const buildPayload = (values) => ({
    salesOrderId: String(values.salesOrderId || "").trim(),
    awbCode: String(values.awbCode || "").trim(),
    issueReason: String(values.issueReason || "").trim(),
    actionType: values.actionType,
    carrier: values.carrier,
    carrierOther: values.carrier === "Other" ? String(values.carrierOther || "").trim() : "",
    escalatedVia: values.escalatedVia,
    escalationDate: startOfDayIso(values.escalationDate),
    nextFollowUpDate: startOfDayIso(values.nextFollowUpDate),
    status: values.status,
    assignedTo: String(values.assignedTo || "").trim(),
    latestRemarks: String(values.latestRemarks || "").trim(),
    resolutionRemarks: String(values.resolutionRemarks || "").trim(),
    resolutionDate: startOfDayIso(values.resolutionDate),
    rejectionReason: String(values.rejectionReason || "").trim(),
    closedDate: startOfDayIso(values.closedDate),
  });

  const submit = async (force = false) => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      return; // antd already highlights invalid fields
    }
    setSaving(true);
    try {
      if (isEdit) {
        await updateCase(undefined, record._id, buildPayload(values), files);
        message.success("Escalation updated");
      } else {
        const res = await createCase(undefined, { ...buildPayload(values), force: force || undefined }, files);
        if (res.requiresConfirmation) {
          Modal.warning({
            title: "Possible duplicate escalation",
            width: 560,
            content: (
              <div>
                <p>{res.message}</p>
                <ul style={{ paddingLeft: 18 }}>
                  {(res.duplicates || []).map((d) => (
                    <li key={d.escalationId}>
                      <b>{d.escalationId}</b> — {d.salesOrderId} {d.awbCode ? `/ ${d.awbCode}` : ""} · {d.issueReason} · {d.status} · {d.assignedTo}
                    </li>
                  ))}
                </ul>
                <p style={{ color: "#8a6d3b" }}>Save anyway? The warning does not block creating this escalation.</p>
              </div>
            ),
            okText: "Save anyway",
            cancelText: "Go back",
            onOk: () => submit(true),
          });
          return;
        }
        message.success("Escalation created");
      }
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      message.error(errMsg(err, "Failed to save escalation"));
    } finally {
      setSaving(false);
    }
  };

  const assigneeOptions = (() => {
    const base = [...(options?.assignees || [])];
    (options?.users || []).forEach((u) => { if (u.name && !base.includes(u.name)) base.push(u.name); });
    return base.map((n) => ({ label: n, value: n }));
  })();

  return (
    <Drawer
      title={isEdit ? `Edit ${record.escalationId}` : "New Escalation / Claim"}
      placement="right"
      width={isMobile ? "100%" : 560}
      open={open}
      onClose={onClose}
      className="ec-drawer"
      extra={
        <Space>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={() => submit(false)}>
            {isEdit ? "Save Changes" : "Create Escalation"}
          </Button>
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Fields marked * are required. Unresolved cases must keep an assigned owner and next follow-up date."
      />
      <Form form={form} layout="vertical" size="middle" className="ec-form">
        <Form.Item name="salesOrderId" label="Sales Order ID" rules={[{ required: true, message: "Sales Order ID is required" }]}>
          <Input placeholder="e.g. S00123" allowClear onBlur={handleValidateOrder} />
        </Form.Item>
        {orderCheck && (
          <Alert type={orderCheck.type} showIcon message={orderCheck.text} style={{ marginTop: -12, marginBottom: 14 }} />
        )}
        <Form.Item name="awbCode" label="AWB / Tracking Code">
          <Input placeholder="AWB number (required when the order has one)" allowClear />
        </Form.Item>
        <Form.Item name="issueReason" label="Issue / Reason" rules={[{ required: true, message: "Issue / reason is required" }]}>
          <Input placeholder="e.g. Package not delivered, damaged parcel…" allowClear />
        </Form.Item>
        <div className="ec-form-row">
          <Form.Item name="actionType" label="Action Type" rules={[{ required: true }]}>
            <Select options={ACTION_TYPES.map((a) => ({ label: a, value: a }))} />
          </Form.Item>
          <Form.Item name="escalatedVia" label="Escalated Via" rules={[{ required: true }]}>
            <Select options={CHANNELS.map((c) => ({ label: c, value: c }))} />
          </Form.Item>
        </div>
        <div className="ec-form-row">
          <Form.Item name="carrier" label="Carrier" rules={[{ required: true }]}>
            <Select options={CARRIERS.map((c) => ({ label: c, value: c }))} />
          </Form.Item>
          <Form.Item name="escalationDate" label="Escalation Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </div>
        {carrierValue === "Other" && (
          <Form.Item name="carrierOther" label="Carrier Name (Other)" rules={[{ required: true, message: "Carrier name is required" }]}>
            <Input placeholder="Enter carrier name" allowClear />
          </Form.Item>
        )}
        <Form.Item name="assignedTo" label="Assigned To" rules={[{ required: true, message: "Assignee is required" }]}>
          <Select
            showSearch
            allowClear
            placeholder="Select or type owner"
            options={assigneeOptions}
            filterOption={(input, option) => String(option?.label || "").toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>
        <Form.Item name="status" label="Status" rules={[{ required: true }]}>
          <Select options={STATUSES.map((s) => ({ label: s, value: s }))} />
        </Form.Item>

        {OPEN_STATUSES.includes(statusValue) && (
          <Form.Item
            name="nextFollowUpDate"
            label="Next Follow-up Date"
            rules={[{ required: true, message: "Next follow-up date is required while unresolved" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        )}
        {statusValue === "Resolved" && (
          <>
            <Form.Item name="resolutionRemarks" label="Resolution Remarks" rules={[{ required: true, message: "Resolution remarks are required" }]}>
              <TextArea rows={3} placeholder="Describe how the case was resolved" />
            </Form.Item>
            <Form.Item name="resolutionDate" label="Resolution Date" rules={[{ required: true, message: "Resolution date is required" }]}>
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </>
        )}
        {statusValue === "Rejected" && (
          <>
            <Form.Item name="rejectionReason" label="Rejection Reason" rules={[{ required: true, message: "Rejection reason is required" }]}>
              <TextArea rows={3} placeholder="Why is this being rejected?" />
            </Form.Item>
            <Form.Item name="closedDate" label="Closure Date" rules={[{ required: true, message: "Closure date is required" }]}>
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </>
        )}
        <Form.Item name="latestRemarks" label="Remarks" rules={[{ required: true, message: "Remarks are required" }]}>
          <TextArea rows={3} placeholder="Latest remarks / internal notes" />
        </Form.Item>
        <Form.Item label="Attachments (optional — images, PDF, Excel, CSV, text)">
          <Upload
            multiple
            beforeUpload={(file) => { setFiles((prev) => [...prev, file]); return false; }}
            onRemove={(file) => setFiles((prev) => prev.filter((f) => f.name !== file.name))}
            fileList={files.map((f, i) => ({ uid: String(i), name: f.name, status: "done" }))}
          >
            <Button icon={<PlusOutlined />}>Add evidence</Button>
          </Upload>
        </Form.Item>
        {isEdit && record.followUpHistory && record.followUpHistory.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`${record.followUpHistory.length} follow-up record(s) exist — history entries are append-only and are not modified by edits.`}
          />
        )}
      </Form>
    </Drawer>
  );
};

// ─────────────────────────────────────────────
// Follow-up modal (append-only history entry)
// ─────────────────────────────────────────────
export const FollowUpModal = ({ open, onClose, record, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const statusAfter = Form.useWatch("statusAfter", form);

  useEffect(() => {
    if (!open) return;
    setFiles([]);
    form.resetFields();
    form.setFieldsValue({
      followUpDate: dayjs(),
      channel: "WhatsApp",
      statusAfter: record && OPEN_STATUSES.includes(record.status) ? record.status : "Need to Follow Up",
      nextFollowUpDate: record && record.nextFollowUpDate ? dayjs(record.nextFollowUpDate) : null,
      contactedParty: record ? record.assignedTo || "" : "",
    });
  }, [open, record, form]);

  const submit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      return;
    }
    setSaving(true);
    try {
      await addFollowUp(undefined, record._id, {
        followUpDate: values.followUpDate ? values.followUpDate.startOf("day").toISOString() : null,
        channel: values.channel,
        contactedParty: String(values.contactedParty || "").trim(),
        outcome: String(values.outcome || "").trim(),
        remarks: String(values.remarks || "").trim(),
        nextFollowUpDate: values.nextFollowUpDate ? values.nextFollowUpDate.startOf("day").toISOString() : null,
        statusAfter: values.statusAfter,
        assignedTo: values.assignedTo ? String(values.assignedTo).trim() : undefined,
      }, files);
      message.success("Follow-up added — case remains open unless resolved separately");
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      message.error(errMsg(err, "Failed to add follow-up"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Add Follow-up — ${record ? record.escalationId : ""}`}
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText="Save Follow-up"
      confirmLoading={saving}
      width={560}
      className="ec-modal"
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 14 }}
        message="Follow-ups are append-only. Completing a follow-up does NOT close the case — use Resolve / Reject for that."
      />
      <Form form={form} layout="vertical" className="ec-form">
        <div className="ec-form-row">
          <Form.Item name="followUpDate" label="Follow-up Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="channel" label="Channel" rules={[{ required: true }]}>
            <Select options={CHANNELS.map((c) => ({ label: c, value: c }))} />
          </Form.Item>
        </div>
        <Form.Item name="contactedParty" label="Contacted Party">
          <Input placeholder="e.g. Carrier support, customer, operations…" allowClear />
        </Form.Item>
        <Form.Item name="outcome" label="Outcome" rules={[{ required: true, message: "Outcome is required" }]}>
          <Input placeholder="e.g. Carrier acknowledged, promised reshipment…" allowClear />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks" rules={[{ required: true, message: "Remarks are required" }]}>
          <TextArea rows={3} placeholder="Details of the conversation / next steps" />
        </Form.Item>
        <div className="ec-form-row">
          <Form.Item name="statusAfter" label="Updated Status" rules={[{ required: true }]}>
            <Select
              options={OPEN_STATUSES.map((s) => ({ label: s, value: s }))}
              disabled={Boolean(record && !OPEN_STATUSES.includes(record.status))}
            />
          </Form.Item>
          <Form.Item
            name="nextFollowUpDate"
            label="Next Follow-up Date"
            rules={[{ required: OPEN_STATUSES.includes(statusAfter || ""), message: "Required while the case stays open" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </div>
        <Form.Item label="Attachments (optional)">
          <Upload
            multiple
            beforeUpload={(file) => { setFiles((prev) => [...prev, file]); return false; }}
            onRemove={(file) => setFiles((prev) => prev.filter((f) => f.name !== file.name))}
            fileList={files.map((f, i) => ({ uid: String(i), name: f.name, status: "done" }))}
          >
            <Button icon={<PlusOutlined />}>Add evidence</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ─────────────────────────────────────────────
// Resolve modal — resolution remarks + date required
// ─────────────────────────────────────────────
export const ResolveModal = ({ open, onClose, record, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({ resolutionDate: dayjs() });
  }, [open, form]);

  const submit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      return;
    }
    setSaving(true);
    try {
      await resolveCase(undefined, record._id, {
        resolutionRemarks: String(values.resolutionRemarks || "").trim(),
        resolutionDate: values.resolutionDate ? values.resolutionDate.startOf("day").toISOString() : null,
      });
      message.success("Case marked as Resolved");
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      message.error(errMsg(err, "Failed to resolve case"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Resolve — ${record ? record.escalationId : ""}`}
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText="Mark Resolved"
      confirmLoading={saving}
      className="ec-modal"
    >
      <Form form={form} layout="vertical" className="ec-form">
        <Form.Item name="resolutionRemarks" label="Resolution Remarks *" rules={[{ required: true, message: "Resolution remarks are required" }]}>
          <TextArea rows={4} placeholder="How was this resolved?" />
        </Form.Item>
        <Form.Item name="resolutionDate" label="Resolution Date *" rules={[{ required: true, message: "Resolution date is required" }]}>
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ─────────────────────────────────────────────
// Reject modal — rejection reason + closure date required
// ─────────────────────────────────────────────
export const RejectModal = ({ open, onClose, record, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({ closedDate: dayjs() });
  }, [open, form]);

  const submit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      return;
    }
    setSaving(true);
    try {
      await rejectCase(undefined, record._id, {
        rejectionReason: String(values.rejectionReason || "").trim(),
        closedDate: values.closedDate ? values.closedDate.startOf("day").toISOString() : null,
      });
      message.success("Case marked as Rejected");
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      message.error(errMsg(err, "Failed to reject case"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Reject — ${record ? record.escalationId : ""}`}
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText="Mark Rejected"
      confirmLoading={saving}
      className="ec-modal"
    >
      <Form form={form} layout="vertical" className="ec-form">
        <Form.Item name="rejectionReason" label="Rejection Reason *" rules={[{ required: true, message: "Rejection reason is required" }]}>
          <TextArea rows={4} placeholder="Why is this case rejected?" />
        </Form.Item>
        <Form.Item name="closedDate" label="Closure Date *" rules={[{ required: true, message: "Closure date is required" }]}>
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ─────────────────────────────────────────────
// Order-wise escalation history modal
// ─────────────────────────────────────────────
export const OrderHistoryModal = ({ open, onClose, salesOrderId, darkTheme = false }) => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !salesOrderId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    fetchOrderHistory(undefined, salesOrderId)
      .then((res) => { if (alive) setCases(res.cases || []); })
      .catch((err) => { if (alive) setError(errMsg(err, "Failed to load order history")); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, salesOrderId]);

  return (
    <Modal
      title={`Order Escalation History — ${salesOrderId || ""}`}
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
      width={720}
      className="ec-modal"
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      <List
        loading={loading}
        dataSource={cases}
        locale={{ emptyText: "No escalations recorded for this order" }}
        renderItem={(item) => (
          <List.Item className="ec-history-item">
            <div style={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <b>{item.escalationId}</b>
                <span>{statusTag(item.status)}</span>
              </div>
              <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
                {item.issueReason} · {item.actionType} · {item.carrierDisplay} · via {item.escalatedVia}
              </div>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                Escalated {fmtDate(item.escalationDate)} · Owner: {item.assignedTo || "—"} · Next follow-up: {fmtDate(item.nextFollowUpDate)} · {item.followUpCount} follow-up(s)
              </div>
            </div>
          </List.Item>
        )}
      />
    </Modal>
  );
};






